import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CheckboxButton from '../../../../components/CheckboxButton';
import { ScrollArea } from '../../../../components/ui/scroll-area';
import CollapsibleCard from '../../../../components/CollapsibleCard';
import TableViewEntry from '../views/TableViewEntry';
import StackViewEntry from '../views/StackViewEntry';
import { setActiveTab, setAdditionalColumns, setSelectedColumns } from '../../../../store/uploads/actions';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../../../components/ui/tooltip';
import constants from '../../../../commonUtils/constants';
import MismatchInfo from './MismatchInfo';
import IconButton from '../../../../components/IconButton';
import { ChevronDoubleLeftIcon } from '@heroicons/react/24/solid';

const AdditionalFields = ({ hideFieldSelection = false, hideNavigationButtons = false }) => {
  const dispatch = useDispatch();
  const { uploadData, additionalColumns, activeTab, destinationDataTypes, errors } = useSelector((state) => state.Uploads);

  const [selectAll, setSelectAll] = useState(false);
  const [selectedCols, setSelectedCols] = useState([]);

  // useEffect(() => {
  //   setSelectedCols(selectedColumns);
  // }, [selectedColumns]);

  useEffect(() => {
    if (additionalColumns.length > 0) {
      const allSelected = additionalColumns.every(col =>
        selectedCols.some(selected => selected.Input_Column === col.Input_Column)
      );
      setSelectAll(allSelected);
    }
  }, [additionalColumns, selectedCols]);

  useEffect(() => {
    dispatch(setSelectedColumns(selectedCols));
  }, [selectedCols, dispatch]);

  const handleCheckboxChange = useCallback((index) => {
    const column = additionalColumns[index];
    const columnIndex = selectedCols.findIndex(col => col.Input_Column === column.Input_Column);

    if (columnIndex >= 0) {
      setSelectedCols(prev => prev.filter(col => col.Input_Column !== column.Input_Column));
    } else {
      setSelectedCols(prev => [...prev, column]);
    }
  }, [selectedCols, additionalColumns]);

  const handleSelectAll = useCallback(() => {
    const newSelectAllState = !selectAll;
    setSelectAll(newSelectAllState);
    setSelectedCols(newSelectAllState ? [...additionalColumns] : []);
  }, [selectAll, additionalColumns]);

  const handleChangeDataType = useCallback((index, value) => {
    if (value) {
      setSelectedCols(prev => {
        const updated = [...prev];
        updated[index] = { ...updated[index], Mapped_DataType: value };
        return updated;
      });
    }
  }, []);

  const handleChangeDestination = useCallback((index, value) => {
    setSelectedCols(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], Mapped_Column: value };
      return updated;
    });
  }, []);

  const moveToMappedFields = useCallback(() => {
    setSelectedCols([]);
    setSelectAll(false);
    dispatch(setSelectedColumns([]));
    dispatch(setAdditionalColumns([]));
    dispatch(setActiveTab("mapped_fields"));
  }, [dispatch]);

  // Memoize the selected columns rendering logic
  const renderSelectedColumns = useMemo(() => {
    if (activeTab !== "additional_fields" || !selectedCols.length) return null;

    return selectedCols.map((item, index) => {
      const hasDataTypeMismatch = item.Input_DataType !== item.Mapped_DataType &&
                                  item.Mapped_DataType !== "" &&
                                  item.Mapped_DataType !== "NA";

      const commonProps = {
        index,
        columns: selectedCols.map(i => i.Input_Column),
        dataTypes: destinationDataTypes,
        source: item.Input_Column,
        sourceType: item.Input_DataType,
        destination: item.Mapped_Column,
        destType: item.Mapped_Column === constants.upload.matchNotFound ? "" : item.Mapped_DataType,
        addColumns: true,
        errors: errors?.selectedColumns?.[index] || []
      };

      return (
        <div
          key={item.Input_Column}
          className={`py-1 px-1 rounded ${hasDataTypeMismatch ? 'bg-yellow-200' : ''}`}
        >
          {/* Desktop view */}
          <div className="hidden md:block">
            <TableViewEntry
              {...commonProps}
              changeMappedField={handleChangeDestination}
              setType={handleChangeDataType}
            />
          </div>
          
          {/* Mobile view */}
          <div className="block md:hidden">
            <StackViewEntry
              {...commonProps}
              setType={handleChangeDataType}
            />
          </div>
        </div>
      );
    });
  }, [activeTab, selectedCols, destinationDataTypes, errors, handleChangeDestination, handleChangeDataType]);

  const PrevBtn = () => (
      <TooltipProvider>
          <Tooltip>
              <TooltipTrigger asChild>
                  <div className="inline">
                      <IconButton
                        className="mr-2"
                        size="iconsm"
                        variant="nav_btn"
                        onClick={moveToMappedFields}
                        Icon={ChevronDoubleLeftIcon}
                        disabled={activeTab === "mapped_fields"}
                      />
                  </div>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                  <p>Move to Mapped Fields</p>
              </TooltipContent>
          </Tooltip>
      </TooltipProvider>
  );
  
  return (
    <CollapsibleCard
      className="m-0"
      title="Additional Fields"
      showCollapseButton={true}
      headerBtnPos="left"
      headerButtons={!hideFieldSelection && !hideNavigationButtons && <PrevBtn/>}
    >
      <div className="flex flex-col gap-2 h-full" style={{ height: `calc(${constants.upload.cardHeight} - 28px)` }}>
        {!uploadData.pending && !hideFieldSelection && (
          <div className="flex-shrink-0">
            <div className="flex justify-between items-end border-b border-primary pb-3 mb-2">
              <span className="leading-4 font-bold">Choose Fields To Create</span>
              {activeTab === "additional_fields" && (
                <CheckboxButton
                  label="Select All"
                  isChecked={selectAll}
                  onClick={handleSelectAll}
                />
              )}
            </div>

            <ScrollArea className="h-[65px]">
              <div className="flex flex-wrap gap-2 h-full">
                {activeTab === "additional_fields" && additionalColumns.map((column, index) => (
                  <CheckboxButton
                    key={column.Input_Column}
                    label={column.Input_Column}
                    isChecked={selectedCols.some(col => col.Input_Column === column.Input_Column)}
                    onClick={() => handleCheckboxChange(index)}
                  />
                ))}
              </div>
            </ScrollArea>
          </div>
        )}

        <div className="flex flex-col flex-grow min-h-0">
          <p className="leading-4 border-b border-primary pb-3 font-bold mb-3">
            Fields Chosen For Creation
          </p>

          <ScrollArea className="flex-grow min-h-0">
            <div className="flex flex-col gap-1">
              {/* Header row - only show for desktop */}
              <div className="hidden md:block">
                <div className="grid grid-cols-3 gap-2 mt-2">
                  <p>Source (File Field)</p>
                  <p>Destination (DB Field)</p>
                  <p>Destination Data Type</p>
                </div>
              </div>
              
              {/* Render selected columns */}
              {renderSelectedColumns}
            </div>
          </ScrollArea>
        </div>

      </div>
      <MismatchInfo className="items-start" />
    </CollapsibleCard>
  );
};

export default AdditionalFields;